/**
 * Connection Status Utilities
 *
 * Computes derived connection status from connection store state.
 * Used by the StartPage to display connection status at a glance.
 */

import type { Connection } from '../../../../types/electron.d';
import type { ActiveConnection } from '../../../connections/models/connectionTypes';

/**
 * Computed status for a connection, combining saved connection info
 * with real-time status from the connection store.
 */
export type ComputedConnectionStatus = {
  /** The saved connection configuration */
  connection: Connection;
  /** Current status: connected, disconnected, connecting, or error */
  status: 'connected' | 'disconnected' | 'connecting' | 'disconnecting' | 'error';
  /** Database name if connected */
  database?: string;
  /** Error message if status is error */
  errorMessage?: string;
  /** Whether a connection operation is in progress */
  isOperating: boolean;
  /** SSH tunnel state, when a tunnel is configured. */
  tunnelState?: 'up' | 'down' | 'connecting' | 'error';
};

/**
 * Compute the display status for a single connection
 */
export function computeConnectionStatus(
  connection: Connection,
  activeConnections: ActiveConnection[],
  operationById: Record<string, 'connecting' | 'disconnecting' | undefined>,
  errorById: Record<string, string | undefined>
): ComputedConnectionStatus {
  const operation = operationById[connection.id];
  const error = errorById[connection.id];
  const activeConnection = activeConnections.find((ac) => ac.id === connection.id);

  // Check for in-progress operations first
  if (operation === 'connecting') {
    return {
      connection,
      status: 'connecting',
      isOperating: true,
    };
  }

  if (operation === 'disconnecting') {
    return {
      connection,
      status: 'disconnecting',
      database: activeConnection?.connection.database,
      isOperating: true,
    };
  }

  // Check for errors
  if (error) {
    return {
      connection,
      status: 'error',
      errorMessage: error,
      isOperating: false,
    };
  }

  // Check if actively connected
  if (activeConnection && activeConnection.status === 'connected') {
    return {
      connection,
      status: 'connected',
      database: activeConnection.connection.database,
      isOperating: false,
      tunnelState: activeConnection.tunnelState,
    };
  }

  // Default to disconnected
  return {
    connection,
    status: 'disconnected',
    isOperating: false,
  };
}

/**
 * Compute status for all connections
 */
export function computeAllConnectionStatuses(
  connections: Connection[],
  activeConnections: ActiveConnection[],
  operationById: Record<string, 'connecting' | 'disconnecting' | undefined>,
  errorById: Record<string, string | undefined>
): ComputedConnectionStatus[] {
  return connections.map((conn) =>
    computeConnectionStatus(conn, activeConnections, operationById, errorById)
  );
}
